import * as React from 'react';
import { Suspense, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { graphql } from 'gatsby';
import { GatsbyImage, type IGatsbyImageData } from 'gatsby-plugin-image';
import lottie from 'lottie-web';
import styled from 'styled-components';

import { Cards } from '@//components/contentful/Cards';
import { Heading as ContentfulHeading } from '@/components/contentful/Heading';
import { TwoUpRichText } from '@/components/contentful/TwoUpRichText';
import { SEO } from '@/components/global/SEO';
import Page from '@/components/v2/Page';
import { V1GridLines, V1PageWrapper } from '@/components/v2/Page/V1PageWrapper';

import { LoomVideo } from '../components/blog/LoomVideo';
import {
  Banner,
  Button,
  CornerLines,
  Heading,
  RunningText,
  Text
} from '../components/design-system';
import { type CustomerQuoteType } from '../components/global/CustomerQuote';
import { ExternalLinks } from '../components/global/ExternalLinks';
import QuotesMultiple from '../components/global/QuotesMultiple';
import { TronDivider } from '../components/global/TronDivider';
import { DataConnectionIlly } from '../components/integrations/DataConnectionIlly';
import {
  IntegrationGridItem,
  type ThirdPartyIntegrationItem
} from '../components/integrations/IntegrationGridItem';
import { type ExternalLinksContentItem } from '../data/externallinks';
import { ANIMATIONS, INTERNAL_LINKS, SIGNUP_LINK } from '../utils';

const AnimationWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 680px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    width: 100%;
  }
`;

const Wrapper = styled(motion.div)`
  width: 100%;
`;

const Inner = styled.div``;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 830px;
  padding: 0 40px;
  margin: 0 auto 80px;
`;

const TopBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const BannerText = styled(Banner)`
  margin-bottom: -10px;
  padding: 0 3px;
  z-index: 1;
  align-self: center;

  span {
    color: ${({ theme }) => theme.palette.fg1200};
    font-family: ${({ theme }) => theme.fontFamily.MONO_ALT};
    font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
    letter-spacing: 0;
    text-transform: capitalize;
  }

  svg {
    transform: rotate(180deg);
    margin-right: 6px;

    path {
      fill: ${({ theme }) => theme.highlightColor};
    }
  }
`;

const Logo = styled.div`
  --cutout-width: 7ch;
  --cutout-left-x: calc(50% - var(--cutout-width) * 0.5 - 1px);
  --cutout-right-x: calc(50% + var(--cutout-width) * 0.5 + 1px);

  position: relative;
  font-size: 24px;
  font-weight: 600;
  padding: 27px 35px 17px;
  margin-bottom: 40px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
    clip-path: polygon(
      0% 0%,
      var(--cutout-left-x) 0%,
      var(--cutout-left-x) 5px,
      var(--cutout-right-x) 5px,
      var(--cutout-right-x) 0%,
      100% 0%,
      100% 100%,
      0% 100%
    );
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0.5px var(--cutout-right-x) auto var(--cutout-left-x);
    height: 8px;
    width: var(--cutout-width);
    border-right: 1px solid ${({ theme }) => theme.palette.fg900};
    border-left: 1px solid ${({ theme }) => theme.palette.fg900};
    transform: translateY(-50%);
  }

  img {
    height: 60px;
  }
`;

const Content = styled.div`
  position: relative;
  margin-bottom: 100px;
`;

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 100px;
  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    flex-direction: column;
    gap: 50px;
  }
`;

const MainContent = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  max-width: ${({ $fullWidth, theme }) =>
    $fullWidth ? theme.maxWidth.PAGE : '500px'};
  padding: 0 40px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    flex-direction: column;
  }
`;

const ExternalLinksWrapper = styled.div`
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  width: 100%;
  padding: 0 40px;
  margin: 0 auto;
`;

const VideoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  width: 100%;
  padding: 0 40px;
  margin: 0 auto;
`;

const TextWrapper = styled(RunningText)`
  max-width: none;
  flex: 1 1 0;
`;

const Right = styled.div`
  flex: 1.5 1 0;
  margin-left: 50px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin-left: 0;
    margin-top: 60px;
    flex: none;
    width: 100%;
  }
`;

const ThirdPartyIntegrations = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 25px;
  margin-top: 50px;
  padding: 0 40px;
`;

const ThirdPartyList = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    flex-direction: column;
  }
`;

const ThirdPartyGridItem = styled(IntegrationGridItem)`
  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    width: 100%;
    flex: none;
  }
`;

const Video = styled.div`
  height: 550px;
  width: 100%;
  position: relative;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    height: 250px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 15px;
  padding-top: 10px;
`;

const StyledAnimation = styled.div`
  display: flex;
  transform: scale(1.2);

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    transform: scale(1.4);
  }
`;
interface IntegrationTemplateProps {
  data: {
    contentfulIntegration: {
      category: string;
      components: Array<
        {
          id: string;
          internal: {
            type: string;
          };
        } & Record<string, unknown>
      >;
      content: {
        childMarkdownRemark: {
          html: string;
        };
      };
      customerQuotes: CustomerQuoteType[];
      description: { description: string };
      docsLink: string;
      docsText?: string;
      externalLinks: ExternalLinksContentItem[];
      illustration?: boolean;
      logo: { url: string };
      lottieFile: {
        contentful_id: string;
        description: string;
        url: string;
      };
      name: string;
      primaryCtaLink?: string;
      primaryCtaText?: string;
      showConnectionImage: boolean;
      slug: string;
      socialImage?: {
        url: string;
      };
      staticImage: {
        gatsbyImage: IGatsbyImageData;
        title: string;
      };
      thirdPartyIntegrations: ThirdPartyIntegrationItem[];
      title: string;
      video?: string;
    };
  };
}

function Component({
  component
}: {
  component: IntegrationTemplateProps['data']['contentfulIntegration']['components'][number];
}) {
  switch (component?.internal?.type) {
    case 'ContentfulHeading':
      // @ts-expect-error not typing since our types are just manually bound to our gql queries
      return <ContentfulHeading {...component} />;

    case 'ContentfulTwoUpRichText':
      // @ts-expect-error not typing since our types are just manually bound to our gql queries
      return <TwoUpRichText {...component} />;

    case 'ContentfulCards':
      // @ts-expect-error not typing since our types are just manually bound to our gql queries
      return <Cards {...component} />;

    default:
      return null;
  }
}

const IntegrationTemplate: React.ComponentType<IntegrationTemplateProps> =
  React.memo(({ data }) => {
    const integrationData = data.contentfulIntegration;
    const LottieRef = useRef<HTMLDivElement>(null);
    const [lottieAnimationData, setLottieAnimationData] = useState(null);

    useEffect(() => {
      //lets check if there is a lottie file, then make sure it is passed as an object to lottie
      const lottieFile = data.contentfulIntegration.lottieFile;

      if (lottieFile && lottieFile.url) {
        fetch(lottieFile.url)
          .then(response => response.json())
          .then(data => {
            setLottieAnimationData(data);
          })
          .catch(error => {
            console.error('Error fetching Lottie data:', error);
          });
      }
    }, [data.contentfulIntegration.lottieFile]);

    useEffect(() => {
      if (LottieRef.current && lottieAnimationData) {
        const instance = lottie.loadAnimation({
          container: LottieRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          animationData: lottieAnimationData
        });

        return () => instance.destroy();
      }
    }, [lottieAnimationData]);

    const actions = (
      <Actions>
        <Button
          link={integrationData.primaryCtaLink || SIGNUP_LINK}
          stylez="loud"
          text={integrationData.primaryCtaText || 'Get started'}
        />
        <Button
          link={integrationData.docsLink}
          text={integrationData.docsText || 'Read the docs'}
        />
      </Actions>
    );

    return (
      <Page
        seo={
          <SEO
            description={integrationData.description.description}
            image={integrationData.socialImage?.url}
            title={`${integrationData.name} - Integration`}
            url={`${INTERNAL_LINKS.Integrations}${integrationData.slug}/`}
          />
        }>
        <V1GridLines>
          <V1PageWrapper>
            <Wrapper {...ANIMATIONS.SimpleFadeIn}>
              <Inner>
                <Info>
                  <TopBanner>
                    <BannerText to={INTERNAL_LINKS.Integrations}>
                      <span>Integration</span>
                    </BannerText>
                    <Logo>
                      <img
                        alt={integrationData.name}
                        src={integrationData.logo.url}
                      />
                    </Logo>
                  </TopBanner>
                  <Heading
                    css={`
                      margin-bottom: 20px;
                      text-align: center;
                    `}
                    renderAs="h1">
                    {integrationData.title}
                  </Heading>
                  <Text
                    css={`
                      text-align: center;
                      max-width: 530px;
                    `}
                    size="large">
                    {integrationData.description.description}
                  </Text>
                  {actions}
                </Info>
                <Content>
                  {data.contentfulIntegration.components?.length ? null : (
                    <TronDivider />
                  )}
                  <ContentInner>
                    <MainContent
                      $fullWidth={
                        !!integrationData.showConnectionImage ||
                        !!lottieAnimationData ||
                        !!integrationData.staticImage
                      }>
                      <TextWrapper>
                        <Heading renderAs="h2">How it works</Heading>
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              integrationData.content.childMarkdownRemark.html
                          }}
                        />
                        {actions}
                      </TextWrapper>
                      {(integrationData.showConnectionImage ||
                        integrationData.thirdPartyIntegrations) && (
                        <Right>
                          {integrationData.name === 'dbt' ? (
                            <DataConnectionIlly
                              name="Snowflake"
                              withdbt={true}
                            />
                          ) : (
                            <DataConnectionIlly name={integrationData.name} />
                          )}
                          {integrationData.thirdPartyIntegrations &&
                            integrationData.thirdPartyIntegrations.length >
                              0 && (
                              <ThirdPartyIntegrations>
                                <Heading
                                  css={`
                                    text-align: center;
                                  `}
                                  renderAs="h4">
                                  Wire-compatible with {integrationData.name}
                                </Heading>
                                <ThirdPartyList>
                                  {integrationData.thirdPartyIntegrations.map(
                                    (item, index) => {
                                      return (
                                        <ThirdPartyGridItem
                                          key={index}
                                          logoUrl={item.logo.url}
                                          name={item.name}
                                          overrideUrl={item.url}
                                          parentIntegration={item.integration}
                                        />
                                      );
                                    }
                                  )}
                                </ThirdPartyList>
                              </ThirdPartyIntegrations>
                            )}
                        </Right>
                      )}
                      {lottieAnimationData && (
                        <Right>
                          <AnimationWrapper>
                            <Suspense fallback={<div />}>
                              <StyledAnimation ref={LottieRef} />
                            </Suspense>
                          </AnimationWrapper>
                        </Right>
                      )}
                      {integrationData.staticImage && !lottieAnimationData && (
                        <Right>
                          <GatsbyImage
                            alt={integrationData.staticImage.title}
                            image={integrationData.staticImage.gatsbyImage}
                          />
                        </Right>
                      )}
                    </MainContent>
                    <div>
                      {data.contentfulIntegration.components?.map(component => (
                        <Component key={component.id} component={component} />
                      ))}
                    </div>
                    {data.contentfulIntegration.components?.length ? (
                      <TronDivider />
                    ) : null}
                    {integrationData.externalLinks && (
                      <ExternalLinksWrapper>
                        <ExternalLinks
                          content={integrationData.externalLinks}
                          label="Learn more"
                        />
                      </ExternalLinksWrapper>
                    )}
                    {integrationData.video && (
                      <VideoWrapper>
                        <Heading
                          css={`
                            margin-bottom: 30px;
                          `}
                          renderAs="h2">
                          In action
                        </Heading>
                        <Video>
                          <CornerLines
                            bottomLeft={true}
                            bottomRight={true}
                            topLeft={true}
                            topRight={true}
                          />
                          <LoomVideo src={integrationData.video} />
                        </Video>
                      </VideoWrapper>
                    )}
                    {integrationData.customerQuotes && (
                      <QuotesMultiple
                        customerQuotes={integrationData.customerQuotes}
                      />
                    )}
                  </ContentInner>
                </Content>
              </Inner>
            </Wrapper>
          </V1PageWrapper>
        </V1GridLines>
      </Page>
    );
  });

export const pageQuery = graphql`
  query ($slug: String!) {
    contentfulIntegration(slug: { eq: $slug }) {
      ...BlockContentfulIntegration
      components {
        ... on ContentfulHeading {
          ...ComponentHeading
        }
        ... on ContentfulCards {
          ...ComponentCards
        }
        ... on ContentfulTwoUpRichText {
          ...ComponentTwoUpRichText
        }
      }
    }
  }
`;

export default IntegrationTemplate;
